@if (!drawerMode) {
  <app-page-breadcrumb [pageTitle]="pageTitle"></app-page-breadcrumb>
}

<ng-container
  *ngIf="drawerMode; then formBody; else pageWrapper"
></ng-container>

<ng-template #pageWrapper>
  <div class="grid grid-cols-1 gap-6">
    <app-component-card
      [title]="pageTitle"
      desc="Complete los datos del producto"
    >
      <ng-container *ngTemplateOutlet="formBody"></ng-container>
    </app-component-card>
  </div>
</ng-template>

<ng-template #formBody>
  <div [class.p-6]="drawerMode">
    <!-- Loading -->
    @if (loading) {
      <div class="flex justify-center items-center py-12">
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"
        ></div>
        <span class="ml-3 text-gray-500 dark:text-gray-400"
          >Cargando datos...</span
        >
      </div>
    }

    <!-- Error -->
    @if (error) {
      <div
        class="mb-4 p-4 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20"
      >
        <div class="flex items-center gap-3">
          <svg
            class="w-5 h-5 text-error-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
          <p class="text-error-600 dark:text-error-400">{{ error }}</p>
        </div>
      </div>
    }

    <!-- Formulario -->
    @if (!loading) {
      <form
        [formGroup]="form"
        (ngSubmit)="guardar()"
      >
        <!-- ===== GRID PRINCIPAL ===== -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- ================= COLUMNA IZQUIERDA ================= -->
          <div class="lg:col-span-2 space-y-6">
            <!-- ========== Información básica ========== -->
            <div
              class="bg-white dark:bg-gray-800/50 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
            >
              <div
                class="mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
              >
                <h3
                  class="text-base font-semibold text-gray-800 dark:text-white"
                >
                  Información básica
                </h3>
              </div>

              <!-- Nombre del Producto -->
              <div>
                <label
                  class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Nombre del Producto <span class="text-error-500">*</span>
                </label>
                <input
                  type="text"
                  formControlName="name"
                  placeholder="Nombre descriptivo del producto"
                  class="dark:bg-dark-900 h-11 w-full rounded-xl border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  [class.border-error-500]="hasError('name')"
                  [class.border-gray-300]="!hasError('name')"
                />
                @if (hasError('name')) {
                  <p class="mt-1.5 text-sm text-error-500">
                    {{ getError('name') }}
                  </p>
                }
              </div>

              <!-- Descripcion -->
              <div class="mt-4">
                <label
                  class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Descripción
                </label>
                <textarea
                  formControlName="description"
                  rows="3"
                  placeholder="Descripción detallada del producto"
                  class="w-full rounded-xl border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:bg-dark-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 resize-y"
                  [class.border-error-500]="
                    hasError('description') || descriptionLength > 500
                  "
                  [class.border-gray-300]="
                    !hasError('description') && descriptionLength <= 500
                  "
                ></textarea>

                <!-- Mensaje de error + contador en la misma fila -->
                <div class="mt-1.5 flex items-start justify-between gap-3">
                  <div class="flex-1">
                    @if (hasError('description')) {
                      <p class="text-sm text-error-500">
                        {{ getError('description') }}
                      </p>
                    } @else if (descriptionLength > 500) {
                      <p class="text-sm text-error-500">
                        La descripción no puede superar los 500 caracteres.
                      </p>
                    }
                  </div>
                  <p
                    class="text-xs tabular-nums whitespace-nowrap"
                    [class.text-gray-500]="descriptionLength <= 450"
                    [class.dark:text-gray-400]="descriptionLength <= 450"
                    [class.text-warning-500]="
                      descriptionLength > 450 && descriptionLength <= 500
                    "
                    [class.text-error-500]="descriptionLength > 500"
                  >
                    {{ descriptionLength }} / 500
                  </p>
                </div>
              </div>

              <!-- Tipo -->
              <div class="mt-4">
                <label
                  class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Tipo <span class="text-error-500">*</span>
                </label>
                <div class="grid gap-4 grid-cols-2 mb-4">
                  <label
                    class="cursor-pointer flex h-32 items-center gap-4 py-4 px-2 border rounded-xl dark:border-gray-700 dark:bg-gray-900"
                    [class.border-indigo-400]="
                      productOrServiceType === 'product'
                    "
                    [class.dark:border-indigo-400]="
                      productOrServiceType === 'product'
                    "
                  >
                    <input
                      type="radio"
                      name="productOrServiceType"
                      (click)="productOrServiceType = 'product'"
                      (click)="cancelLastProductOrServiceSelection()"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="w-12 h-12 rounded-xl p-2 bg-blue-200 text-blue-800"
                    >
                      <path
                        stroke="none"
                        d="M0 0h24v24H0z"
                        fill="none"
                      />
                      <path d="M12 3l8 4.5l0 9l-8 4.5l-8 -4.5l0 -9l8 -4.5" />
                      <path d="M12 12l8 -4.5" />
                      <path d="M12 12l0 9" />
                      <path d="M12 12l-8 -4.5" />
                      <path d="M16 5.25l-8 4.5" />
                    </svg>
                    <div>
                      <p
                        class="text-base font-semibold text-gray-800 dark:text-white"
                      >
                        Productos
                      </p>
                      <p
                        class="text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Bienes físicos o digirales que se venden.
                      </p>
                    </div>
                  </label>

                  <label
                    class="cursor-pointer flex h-32 items-center gap-4 py-4 px-2 border rounded-xl dark:border-gray-700 dark:bg-gray-900"
                    [class.dark:border-yellow-400]="
                      productOrServiceType === 'service'
                    "
                    [class.border-yellow-400]="
                      productOrServiceType === 'service'
                    "
                  >
                    <input
                      type="radio"
                      name="productOrServiceType"
                      (click)="productOrServiceType = 'service'"
                      (click)="cancelLastProductOrServiceSelection()"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="w-12 h-12 rounded-xl p-2 bg-yellow-200 text-yellow-800"
                    >
                      <path
                        stroke="none"
                        d="M0 0h24v24H0z"
                        fill="none"
                      />
                      <path d="M5 7a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
                      <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      <path d="M21 21v-2a4 4 0 0 0 -3 -3.85" />
                    </svg>
                    <div>
                      <p
                        class="text-base font-semibold text-gray-800 dark:text-white"
                      >
                        Servicios
                      </p>
                      <p
                        class="text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Servicios que se ofrecen a clientes.
                      </p>
                    </div>
                  </label>
                </div>
                <div class="grid grid-cols-2 gap-4">
                  @if (productOrServiceType === 'product') {
                    <label
                      class="cursor-pointer flex h-32 items-center gap-4 py-4 px-2 border rounded-xl dark:border-gray-700 dark:bg-gray-900"
                      [class.border-indigo-400]="
                        productOrServiceSelected === 'inventory'
                      "
                      [class.dark:border-indigo-400]="
                        productOrServiceSelected === 'inventory'
                      "
                    >
                      <input
                        type="radio"
                        name="typeSelection"
                        formControlName="typeSelection"
                        value="inventory"
                        (click)="productOrServiceSelected = 'inventory'"
                      />
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="w-12 h-12 rounded-xl p-2 bg-blue-200 text-blue-800"
                      >
                        <path
                          stroke="none"
                          d="M0 0h24v24H0z"
                          fill="none"
                        />
                        <path d="M12 3l8 4.5l0 9l-8 4.5l-8 -4.5l0 -9l8 -4.5" />
                        <path d="M12 12l8 -4.5" />
                        <path d="M12 12l0 9" />
                        <path d="M12 12l-8 -4.5" />
                      </svg>
                      <div>
                        <p
                          class="text-base font-semibold text-gray-800 dark:text-white"
                        >
                          Con inventario
                        </p>
                        <p
                          class="text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Se gestionan con stocks y control de inventario.
                        </p>
                      </div>
                    </label>

                    <label
                      class="cursor-pointer flex h-32 items-center gap-4 py-4 px-2 border rounded-xl dark:border-gray-700 dark:bg-gray-900"
                      [class.border-indigo-400]="
                        productOrServiceSelected === 'no_inventory'
                      "
                      [class.dark:border-indigo-400]="
                        productOrServiceSelected === 'no_inventory'
                      "
                    >
                      <input
                        type="radio"
                        name="typeSelection"
                        formControlName="typeSelection"
                        value="no_inventory"
                        (click)="productOrServiceSelected = 'no_inventory'"
                      />

                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="w-12 h-12 rounded-xl p-2 bg-blue-200 text-blue-800"
                      >
                        <path
                          stroke="none"
                          d="M0 0h24v24H0z"
                          fill="none"
                        />
                        <path
                          d="M17.765 17.757l-5.765 3.243l-8 -4.5v-9l2.236 -1.258m2.57 -1.445l3.194 -1.797l8 4.5v8.5"
                        />
                        <path d="M14.561 10.559l5.439 -3.059" />
                        <path d="M12 12v9" />
                        <path d="M12 12l-8 -4.5" />
                        <path d="M3 3l18 18" />
                      </svg>
                      <div>
                        <p
                          class="text-base font-semibold text-gray-800 dark:text-white"
                        >
                          Sin inventario
                        </p>
                        <p
                          class="text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          No requieren control de stock.
                        </p>
                      </div>
                    </label>
                  } @else if (productOrServiceType === 'service') {
                    <label
                      class="cursor-pointer flex h-32 items-center gap-4 py-4 px-2 border rounded-xl dark:border-gray-700 dark:bg-gray-900"
                      [class.border-yellow-400]="
                        productOrServiceSelected === 'resources'
                      "
                      [class.dark:border-yellow-400]="
                        productOrServiceSelected === 'resources'
                      "
                    >
                      <input
                        type="radio"
                        name="typeSelection"
                        formControlName="typeSelection"
                        value="resources"
                        (click)="productOrServiceSelected = 'resources'"
                      />
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="w-12 h-12 rounded-xl p-2 bg-yellow-200 text-yellow-800"
                      >
                        <path
                          stroke="none"
                          d="M0 0h24v24H0z"
                          fill="none"
                        />
                        <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
                        <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
                      </svg>
                      <div>
                        <p
                          class="text-base font-semibold text-gray-800 dark:text-white"
                        >
                          Recursos
                        </p>
                        <p
                          class="text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Servicios por recursos (horas, días, etc).
                        </p>
                      </div>
                    </label>

                    <label
                      class="cursor-pointer flex h-32 items-center gap-4 py-4 px-2 border rounded-xl dark:border-gray-700 dark:bg-gray-900"
                      [class.border-yellow-400]="
                        productOrServiceSelected === 'consulting'
                      "
                      [class.dark:border-yellow-400]="
                        productOrServiceSelected === 'consulting'
                      "
                    >
                      <input
                        type="radio"
                        name="typeSelection"
                        formControlName="typeSelection"
                        value="consulting"
                        (click)="productOrServiceSelected = 'consulting'"
                      />

                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="w-12 h-12 rounded-xl p-2 bg-yellow-200 text-yellow-800"
                      >
                        <path
                          stroke="none"
                          d="M0 0h24v24H0z"
                          fill="none"
                        />
                        <path
                          d="M3 9a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2l0 -9"
                        />
                        <path d="M8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2" />
                        <path d="M12 12l0 .01" />
                        <path d="M3 13a20 20 0 0 0 18 0" />
                      </svg>
                      <div>
                        <p
                          class="text-base font-semibold text-gray-800 dark:text-white"
                        >
                          Consultoria
                        </p>
                        <p
                          class="text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Servicios de consultoria por proyecto o alcance.
                        </p>
                      </div>
                    </label>
                  }
                </div>

                @if (hasError('type')) {
                  <p class="mt-1.5 text-sm text-error-500">
                    {{ getError('type') }}
                  </p>
                }
              </div>

              <!-- Referencia -->
              <div class="mt-4">
                <label
                  class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Referencia (ref)
                </label>
                <input
                  type="text"
                  formControlName="ref"
                  placeholder="Código de referencia interno"
                  class="dark:bg-dark-900 h-11 w-full rounded-xl border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                />
                @if (hasError('ref')) {
                  <p class="mt-1.5 text-sm text-error-500">
                    {{ getError('ref') }}
                  </p>
                }
              </div>

              <!-- SKU Prefix (visible solo si sku está vacío) -->
              @if (skuIsEmpty) {
                <div class="mt-4">
                  <label
                    class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Prefijo de SKU
                  </label>
                  <input
                    type="text"
                    formControlName="skuPrefix"
                    placeholder="PROD-"
                    class="dark:bg-dark-900 h-11 w-full rounded-xl border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  />
                  @if (hasError('skuPrefix')) {
                    <p class="mt-1.5 text-sm text-error-500">
                      {{ getError('skuPrefix') }}
                    </p>
                  }
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Prefijo para el SKU auto-generado (máx. 20 caracteres)
                  </p>
                </div>
              }

              <!-- Categoría e Imagen URL -->
              <div class="mt-4">
                <!-- Categoria -->
                <div>
                  <label
                    class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Categoría
                  </label>

                  <!-- Trigger del dropdown -->
                  <div class="relative">
                    <button
                      type="button"
                      (click)="categoriasDropdownOpen = !categoriasDropdownOpen"
                      class="h-auto min-h-11 w-full rounded-xl border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 transition-all duration-200 flex items-start gap-2 flex-wrap text-left"
                    >
                      @if (categoriasSeleccionadas.length === 0) {
                        <span
                          class="text-gray-400 dark:text-white/30 self-center"
                          >Sin categoría</span
                        >
                      }
                      @for (cat of categoriasSeleccionadas; track cat.id) {
                        <span
                          class="inline-flex items-center gap-1 rounded-md bg-brand-50 dark:bg-brand-500/10 px-2 py-0.5 text-xs font-medium text-brand-700 dark:text-brand-300 border border-brand-200 dark:border-brand-500/20"
                        >
                          {{ cat.name }}
                          <span
                            (click)="removerCategoria(cat.id, $event)"
                            class="ml-0.5 hover:text-brand-900 dark:hover:text-white cursor-pointer leading-none"
                            >✕</span
                          >
                        </span>
                      }
                      <!-- Ícono chevron -->
                      <svg
                        class="ml-auto self-center w-4 h-4 text-gray-400 shrink-0 transition-transform"
                        [class.rotate-180]="categoriasDropdownOpen"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    <!-- Panel desplegable -->
                    @if (categoriasDropdownOpen) {
                      <!-- Overlay para cerrar al hacer click fuera -->
                      <div
                        class="fixed inset-0 z-10"
                        (click)="categoriasDropdownOpen = false"
                      ></div>

                      <div
                        class="absolute z-20 mt-1 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg max-h-52 overflow-y-auto"
                      >
                        @if (loadingCategorias) {
                          <div class="px-4 py-3 text-sm text-gray-400">
                            Cargando categorías...
                          </div>
                        }
                        @if (!loadingCategorias && categorias.length === 0) {
                          <div class="px-4 py-3 text-sm text-gray-400">
                            No hay categorías disponibles
                          </div>
                        }
                        @for (cat of categorias; track cat.id) {
                          <button
                            type="button"
                            (click)="toggleCategoria(cat.id)"
                            class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                            [class.text-brand-600]="
                              isCategoriaSeleccionada(cat.id)
                            "
                            [class.dark:text-brand-400]="
                              isCategoriaSeleccionada(cat.id)
                            "
                            [class.text-gray-700]="
                              !isCategoriaSeleccionada(cat.id)
                            "
                            [class.dark:text-gray-300]="
                              !isCategoriaSeleccionada(cat.id)
                            "
                          >
                            <!-- Checkbox visual -->
                            <span
                              class="flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors"
                              [class.bg-brand-500]="
                                isCategoriaSeleccionada(cat.id)
                              "
                              [class.border-brand-500]="
                                isCategoriaSeleccionada(cat.id)
                              "
                              [class.border-gray-300]="
                                !isCategoriaSeleccionada(cat.id)
                              "
                              [class.dark:border-gray-600]="
                                !isCategoriaSeleccionada(cat.id)
                              "
                            >
                              @if (isCategoriaSeleccionada(cat.id)) {
                                <svg
                                  class="w-2.5 h-2.5 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="3"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              }
                            </span>
                            {{ cat.name }}
                          </button>
                        }
                      </div>
                    }
                  </div>

                  @if (
                    loadingCategorias && categoriasSeleccionadas.length === 0
                  ) {
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Cargando categorías...
                    </p>
                  }
                </div>
              </div>
            </div>

            <!-- ========== Precios y costos ========== -->
            <div
              class="bg-white dark:bg-gray-800/50 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
            >
              <div
                class="mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
              >
                <h4
                  class="text-base font-semibold text-gray-800 dark:text-white"
                >
                  Precios y costos
                </h4>
              </div>

              <!-- Moneda -->
              <div>
                <label
                  class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Moneda <span class="text-error-500">*</span>
                </label>
                <select
                  formControlName="currencyId"
                  class="dark:bg-dark-900 h-11 w-full rounded-xl border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  [class.border-error-500]="hasError('currencyId')"
                  [class.border-gray-300]="!hasError('currencyId')"
                >
                  <option
                    [ngValue]="null"
                    disabled
                  >
                    Selecciona una moneda
                  </option>
                  @for (moneda of monedas; track moneda.id) {
                    <option [ngValue]="moneda.id">
                      {{ moneda.name }} ({{ moneda.code }} {{ moneda.prefix }})
                    </option>
                  }
                </select>
                @if (hasError('currencyId')) {
                  <p class="mt-1.5 text-sm text-error-500">
                    {{ getError('currencyId') }}
                  </p>
                }
                @if (loadingMonedas) {
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Cargando monedas...
                  </p>
                }
              </div>

              <!-- Precio, Costo y Precio mínimo -->
              <div class="grid grid-cols-1 gap-5 sm:grid-cols-3 mt-5">
                <!-- Precio -->
                <div>
                  <label
                    class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Precio de venta <span class="text-error-500">*</span>
                  </label>
                  <div class="relative">
                    @if (selectedPrefix) {
                      <span
                        class="absolute left-0 top-1/2 -translate-y-1/2 border-r border-gray-200 px-3.5 py-3 text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400"
                      >
                        {{ selectedPrefix }}
                      </span>
                    }
                    <input
                      type="number"
                      formControlName="price"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      class="dark:bg-dark-900 h-11 w-full rounded-xl border bg-transparent py-2.5 pr-4 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      [class.pl-[62px]]="selectedPrefix"
                      [class.px-4]="!selectedPrefix"
                      [class.border-error-500]="hasError('price')"
                      [class.border-gray-300]="!hasError('price')"
                    />
                  </div>
                  @if (hasError('price')) {
                    <p class="mt-1.5 text-sm text-error-500">
                      {{ getError('price') }}
                    </p>
                  }
                </div>

                <!-- Costo -->
                <div>
                  <label
                    class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Costo
                  </label>
                  <div class="relative">
                    @if (selectedPrefix) {
                      <span
                        class="absolute left-0 top-1/2 -translate-y-1/2 border-r border-gray-200 px-3.5 py-3 text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400"
                      >
                        {{ selectedPrefix }}
                      </span>
                    }
                    <input
                      type="number"
                      formControlName="cost"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      class="dark:bg-dark-900 h-11 w-full rounded-xl border border-gray-300 bg-transparent py-2.5 pr-4 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      [class.pl-[62px]]="selectedPrefix"
                      [class.px-4]="!selectedPrefix"
                    />
                  </div>
                  @if (hasError('cost')) {
                    <p class="mt-1.5 text-sm text-error-500">
                      {{ getError('cost') }}
                    </p>
                  }
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Precio de adquisición (para margen)
                  </p>
                </div>

                <!-- Precio mínimo -->
                <div>
                  <label
                    class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Precio mínimo
                  </label>
                  <div class="relative">
                    @if (selectedPrefix) {
                      <span
                        class="absolute left-0 top-1/2 -translate-y-1/2 border-r border-gray-200 px-3.5 py-3 text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400"
                      >
                        {{ selectedPrefix }}
                      </span>
                    }
                    <input
                      type="number"
                      formControlName="minPrice"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      class="dark:bg-dark-900 h-11 w-full rounded-xl border border-gray-300 bg-transparent py-2.5 pr-4 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      [class.pl-[62px]]="selectedPrefix"
                      [class.px-4]="!selectedPrefix"
                    />
                  </div>
                  @if (hasError('minPrice')) {
                    <p class="mt-1.5 text-sm text-error-500">
                      {{ getError('minPrice') }}
                    </p>
                  }
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Precio mínimo permitido en cotizaciones
                  </p>
                </div>
              </div>
            </div>

            <!-- ========== Impuestos y descuentos ========== -->
            <div
              class="bg-white dark:bg-gray-800/50 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
            >
              <div
                class="mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
              >
                <h4
                  class="text-base font-semibold text-gray-800 dark:text-white"
                >
                  Impuestos y descuentos
                </h4>
              </div>

              <div class="grid grid-cols-1 gap-5 sm:grid-cols-3">
                <!-- Impuesto % -->
                <div>
                  <label
                    class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Impuesto (%)
                  </label>
                  <input
                    type="number"
                    formControlName="taxPercent"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    class="dark:bg-dark-900 h-11 w-full rounded-xl border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  />
                  @if (hasError('taxPercent')) {
                    <p class="mt-1.5 text-sm text-error-500">
                      {{ getError('taxPercent') }}
                    </p>
                  }
                </div>

                <!-- Descuento máximo % -->
                <div>
                  <label
                    class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Descuento máximo (%)
                  </label>
                  <input
                    type="number"
                    formControlName="maxDiscountPercent"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="0.00"
                    class="dark:bg-dark-900 h-11 w-full rounded-xl border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  />
                  @if (hasError('maxDiscountPercent')) {
                    <p class="mt-1.5 text-sm text-error-500">
                      {{ getError('maxDiscountPercent') }}
                    </p>
                  }
                </div>

                <!-- Gravable checkbox -->
                <div class="flex items-start gap-3 pt-2 sm:pt-7">
                  <input
                    type="checkbox"
                    formControlName="isTaxable"
                    id="isTaxable"
                    class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/10 dark:border-gray-600 dark:bg-gray-800 transition-all duration-200"
                  />
                  <div>
                    <label
                      for="isTaxable"
                      class="text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Gravable
                    </label>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      El producto aplica impuesto
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- ========== Producto físico (solo cuando tipo = product) ========== -->
            @if (isProduct) {
              <div
                class="bg-white dark:bg-gray-800/50 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
              >
                <div
                  class="mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                  <h4
                    class="text-base font-semibold text-gray-800 dark:text-white"
                  >
                    Producto físico
                  </h4>
                </div>

                <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <!-- Unidad de medida -->
                  <div>
                    <label
                      class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Unidad de medida
                    </label>
                    <input
                      type="text"
                      formControlName="unitOfMeasure"
                      placeholder="unidad, kg, litro..."
                      class="dark:bg-dark-900 h-11 w-full rounded-xl border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    />
                    @if (hasError('unitOfMeasure')) {
                      <p class="mt-1.5 text-sm text-error-500">
                        {{ getError('unitOfMeasure') }}
                      </p>
                    }
                  </div>

                  <!-- Rastrear inventario -->
                  <div class="flex items-start gap-3 pt-2 sm:pt-7">
                    <input
                      type="checkbox"
                      formControlName="isTrackable"
                      id="isTrackable"
                      class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/10 dark:border-gray-600 dark:bg-gray-800 transition-all duration-200"
                    />
                    <div>
                      <label
                        for="isTrackable"
                        class="text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Rastrear inventario
                      </label>
                      <p class="text-xs text-gray-500 dark:text-gray-400">
                        Controlar stock del producto
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Stock actual y nivel mínimo (solo si isTrackable) -->
                @if (form.get('isTrackable')?.value) {
                  <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 mt-5">
                    <!-- Stock actual -->
                    <div>
                      <label
                        class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Cantidad de stock
                      </label>
                      <input
                        type="number"
                        formControlName="stockQuantity"
                        step="0.01"
                        min="0"
                        placeholder="0"
                        class="dark:bg-dark-900 h-11 w-full rounded-xl border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      />
                      @if (hasError('stockQuantity')) {
                        <p class="mt-1.5 text-sm text-error-500">
                          {{ getError('stockQuantity') }}
                        </p>
                      }
                    </div>

                    <!-- Nivel mínimo de stock -->
                    <div>
                      <label
                        class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Nivel mínimo de stock
                      </label>
                      <input
                        type="number"
                        formControlName="minStockLevel"
                        step="0.01"
                        min="0"
                        placeholder="0"
                        class="dark:bg-dark-900 h-11 w-full rounded-xl border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      />
                      @if (hasError('minStockLevel')) {
                        <p class="mt-1.5 text-sm text-error-500">
                          {{ getError('minStockLevel') }}
                        </p>
                      }
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Alerta cuando el stock baje de este nivel
                      </p>
                    </div>
                  </div>
                }
              </div>
            }

            <!-- ========== Servicio ========== -->
            @if (isService) {
              <div
                class="bg-white dark:bg-gray-800/50 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
              >
                <div
                  class="mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                  <h4
                    class="text-base font-semibold text-gray-800 dark:text-white"
                  >
                    Servicio
                  </h4>
                </div>

                <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <!-- Duración en horas -->
                  <div>
                    <label
                      class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Duración (horas)
                    </label>
                    <input
                      type="number"
                      formControlName="durationHours"
                      step="0.5"
                      min="0"
                      placeholder="0"
                      class="dark:bg-dark-900 h-11 w-full rounded-xl border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    />
                    @if (hasError('durationHours')) {
                      <p class="mt-1.5 text-sm text-error-500">
                        {{ getError('durationHours') }}
                      </p>
                    }
                  </div>

                  <!-- Es Recurrente -->
                  <div class="flex items-start gap-3 pt-2 sm:pt-7">
                    <input
                      type="checkbox"
                      formControlName="isRecurring"
                      id="isRecurring"
                      class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/10 dark:border-gray-600 dark:bg-gray-800 transition-all duration-200"
                    />
                    <div>
                      <label
                        for="isRecurring"
                        class="text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Servicio recurrente
                      </label>
                      <p class="text-xs text-gray-500 dark:text-gray-400">
                        El servicio se cobra de forma periódica
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Ciclo de cobro (solo si es recurrente) -->
                @if (isRecurringValue) {
                  <div class="mt-5">
                    <label
                      class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Ciclo de cobro
                    </label>
                    <select
                      formControlName="billingCycle"
                      class="dark:bg-dark-900 h-11 w-full rounded-xl border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <option value="monthly">Mensual</option>
                      <option value="quarterly">Trimestral</option>
                      <option value="yearly">Anual</option>
                      <option value="one-time">Una sola vez</option>
                    </select>
                  </div>

                  <!-- Condición de facturación -->
                  <div class="mt-5">
                    <label
                      class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Condición de facturación
                    </label>
                    <select
                      formControlName="billingCondition"
                      class="dark:bg-dark-900 h-11 w-full rounded-xl border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <option value="auto_renew">Renovación automática</option>
                      <option value="fixed_payments">Pagos fijos</option>
                    </select>
                  </div>

                  <!-- Número de pagos fijos -->
                  @if (isFixedPayments) {
                    <div class="mt-5">
                      <label
                        class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Número de pagos
                      </label>
                      <input
                        type="number"
                        formControlName="fixedPaymentCount"
                        min="1"
                        placeholder="12"
                        class="dark:bg-dark-900 h-11 w-full rounded-xl border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                        [class.border-error-500]="hasError('fixedPaymentCount')"
                        [class.border-gray-300]="!hasError('fixedPaymentCount')"
                      />
                      @if (hasError('fixedPaymentCount')) {
                        <p class="mt-1.5 text-sm text-error-500">
                          {{ getError('fixedPaymentCount') }}
                        </p>
                      }
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Cantidad de pagos hasta completar el cobro
                      </p>
                    </div>
                  }
                }
              </div>
            }

            <!-- ========== Opciones avanzadas ========== -->
            <div
              class="bg-white dark:bg-gray-800/50 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
            >
              <div
                class="mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
              >
                <h4
                  class="text-base font-semibold text-gray-800 dark:text-white"
                >
                  Opciones avanzadas
                </h4>
              </div>

              <div class="flex items-start gap-3">
                <input
                  type="checkbox"
                  formControlName="usesTierPricing"
                  id="usesTierPricing"
                  class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/10 dark:border-gray-600 dark:bg-gray-800 transition-all duration-200"
                />
                <div>
                  <label
                    for="usesTierPricing"
                    class="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Usar precios escalonados
                  </label>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Los precios escalonados se configuran al editar el
                    producto, en la sección «Precios escalonados»
                  </p>
                </div>
              </div>
            </div>

            <!-- ========== Vigencia promocional (Incidente #33) ========== -->
            <div
              class="bg-white dark:bg-gray-800/50 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
            >
              <div
                class="mb-4 pb-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between"
              >
                <h4
                  class="text-base font-semibold text-gray-800 dark:text-white"
                >
                  Vigencia promocional
                </h4>
                <div class="flex items-center gap-2">
                  <span
                    class="text-xs font-medium px-2 py-0.5 rounded-full"
                    [class]="promoStatus.classes"
                    >{{ promoStatus.text }}</span
                  >
                  @if (tieneVigenciaPromo || promoPriceValue !== null) {
                    <button
                      type="button"
                      (click)="removePromotion()"
                      class="text-xs font-medium text-error-500 hover:underline"
                    >
                      Quitar promoción
                    </button>
                  }
                </div>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
                La promoción se activa y desactiva automáticamente según estas
                fechas.
              </p>

              <!-- Precio promocional + interruptor (Incidente #56) -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label
                    for="promoPrice"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >Precio promocional
                    @if (tieneVigenciaPromo) {
                      <span class="text-error-500">*</span>
                    }</label
                  >
                  <div class="relative">
                    @if (selectedPrefix) {
                      <span
                        class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-500 dark:text-gray-400"
                        >{{ selectedPrefix }}</span
                      >
                    }
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      formControlName="promoPrice"
                      id="promoPrice"
                      placeholder="Sin promoción"
                      class="w-full rounded-lg border bg-white dark:bg-gray-800 py-2 pr-3 text-sm text-gray-800 dark:text-gray-100 focus:border-brand-500 focus:outline-none"
                      [class.pl-10]="selectedPrefix"
                      [class.pl-3]="!selectedPrefix"
                      [class.border-error-500]="
                        promoPriceInvalid || promoPriceRequiredMissing
                      "
                      [class.border-gray-300]="
                        !promoPriceInvalid && !promoPriceRequiredMissing
                      "
                      [class.dark:border-gray-600]="
                        !promoPriceInvalid && !promoPriceRequiredMissing
                      "
                    />
                  </div>
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Debe ser menor que el precio de venta. Obligatorio si
                    defines una vigencia.
                  </p>
                </div>
                <div class="flex items-start gap-3 sm:pt-7">
                  <input
                    type="checkbox"
                    formControlName="promoEnabled"
                    id="promoEnabled"
                    class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/10 dark:border-gray-600 dark:bg-gray-800 transition-all duration-200"
                  />
                  <div>
                    <label
                      for="promoEnabled"
                      class="text-sm font-medium text-gray-700 dark:text-gray-300"
                      >Promoción activa</label
                    >
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      Desactívala para suspender la promoción sin borrar sus
                      fechas.
                    </p>
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    for="promoValidFrom"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >Inicio de vigencia</label
                  >
                  <input
                    type="date"
                    [min]="minDatetimeLocal"
                    formControlName="promoValidFrom"
                    (change)="promoValidDateChange()"
                    class="dark:[&::-webkit-calendar-picker-indicator]:invert dark:[&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:appearance-auto! [&::-webkit-calendar-picker-indicator]:block! dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    [class.border-error-500]="
                      hasError('promoValidFrom') || promoDatesRequiredMissing
                    "
                    [class.border-gray-300]="
                      !hasError('promoValidFrom') && !promoDatesRequiredMissing
                    "
                  />
                  @if (hasError('promoValidFrom')) {
                    <p class="mt-1.5 text-sm text-error-500">
                      {{ getError('promoValidFrom') }}
                    </p>
                  }
                </div>
                <div>
                  <label
                    for="promoValidUntil"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >Fin de vigencia</label
                  >
                  <input
                    type="date"
                    [min]="minDatetimeLocal"
                    formControlName="promoValidUntil"
                    (change)="promoValidDateChange()"
                    class="dark:[&::-webkit-calendar-picker-indicator]:invert dark:[&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:appearance-auto! [&::-webkit-calendar-picker-indicator]:block! dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    [class.border-error-500]="
                      hasError('promoValidUntil') || promoDatesRequiredMissing
                    "
                    [class.border-gray-300]="
                      !hasError('promoValidUntil') && !promoDatesRequiredMissing
                    "
                  />
                  @if (hasError('promoValidUntil')) {
                    <p class="mt-1.5 text-sm text-error-500">
                      {{ getError('promoValidUntil') }}
                    </p>
                  }
                </div>
              </div>
              @if (promoRangeInvalid) {
                <p class="mt-2 text-xs text-error-500">
                  La fecha de fin de vigencia no puede ser anterior a la fecha
                  de inicio.
                </p>
              }
              @if (promoPriceInvalid) {
                <p class="mt-2 text-xs text-error-500">
                  El precio promocional debe ser mayor que 0 y menor que el
                  precio de venta.
                </p>
              }
              @if (promoDatesRequiredMissing) {
                <p class="mt-2 text-xs text-error-500">
                  Para definir un precio promocional debes indicar la fecha de
                  inicio y la de fin de vigencia.
                </p>
              }
              @if (promoPriceRequiredMissing) {
                <p class="mt-2 text-xs text-error-500">
                  Para definir una vigencia promocional debes indicar el precio
                  promocional; si no, el producto se mostraría con el precio
                  tachado y sin precio actual.
                </p>
              }
            </div>

            <!-- ========== Estado (Incidente #1: 4 estados) ========== -->
            <div
              class="bg-white dark:bg-gray-800/50 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
            >
              <label
                for="status"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
              >
                Estado del producto
              </label>
              <select
                id="status"
                formControlName="status"
                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="draft">Borrador</option>
                <option value="active">Activo</option>
                <option value="discontinued">Descontinuado</option>
                <option value="archived">Archivado</option>
              </select>
              @if (form.get('status')?.value === 'draft') {
                <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  Solo visible para administradores.
                </p>
              } @else if (form.get('status')?.value === 'discontinued') {
                <p class="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
                  No seleccionable en cotizaciones nuevas; sigue visible en las
                  existentes.
                </p>
              } @else if (form.get('status')?.value === 'archived') {
                <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  Oculto de los listados estándar; solo accesible por
                  administradores con filtro especial.
                </p>
              } @else {
                <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  Visible para todos y seleccionable en cotizaciones.
                </p>
              }
            </div>
          </div>

          <!-- ================= COLUMNA DERECHA ================= -->
          <div class="lg:col-span-1">
            <div class="sticky top-6 space-y-4">
              <!-- SKU Card -->
              <div
                class="bg-white dark:bg-gray-800/50 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
              >
                <h4
                  class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3 tracking-wide"
                >
                  SKU
                </h4>

                <input
                  type="text"
                  formControlName="sku"
                  placeholder="Código único del producto"
                  class="dark:bg-dark-900 h-11 w-full rounded-xl border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-sm text-center font-mono tracking-wider focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  [class.border-error-500]="hasError('sku')"
                  [class.border-gray-300]="!hasError('sku')"
                />

                @if (hasError('sku')) {
                  <p class="mt-1.5 text-sm text-error-500 text-center">
                    {{ getError('sku') }}
                  </p>
                }

                <p
                  class="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center"
                >
                  Se auto-genera si se deja vacío
                </p>
              </div>

              <!-- Barcode Card -->
              @if (skuValue && skuValue.trim()) {
                <div
                  class="bg-white dark:bg-gray-800/50 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 text-center"
                >
                  <h4
                    class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3 tracking-wide"
                  >
                    Código de barras
                  </h4>

                  <div
                    class="flex justify-center items-center p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                  >
                    <app-barcode
                      [value]="skuValue"
                      [width]="2"
                      [height]="50"
                    >
                    </app-barcode>
                  </div>
                </div>
              }
              <!-- Image -->
              <div
                class="bg-white dark:bg-gray-800/50 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 text-start"
              >
                <h4
                  class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3 tracking-wide"
                >
                  Imagen
                </h4>

                <div class="flex-col items-start gap-4">
                  <!-- Preview -->
                  <div
                    class="mb-4 shrink-0 w-full h-48 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-center overflow-hidden"
                  >
                    @if (imagePreviewUrl) {
                      <img
                        [src]="imagePreviewUrl"
                        alt="Image"
                        class="max-w-full max-h-full object-contain"
                      />
                    } @else {
                      <svg
                        class="w-8 h-8 text-gray-300 dark:text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="1.5"
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        ></path>
                      </svg>
                    }
                  </div>

                  <div class="flex-1 space-y-2">
                    <div class="flex items-center gap-2">
                      <label
                        class="px-3 py-1.5 text-sm rounded-lg font-medium transition-colors bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer inline-flex items-center gap-1.5"
                        [class.opacity-50]="imageUploading"
                        [class.pointer-events-none]="imageUploading"
                      >
                        @if (imageUploading) {
                          <div
                            class="animate-spin rounded-full h-4 w-4 border-b-2 border-current"
                          ></div>
                          Subiendo...
                        } @else {
                          <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                            ></path>
                          </svg>
                          Subir Imagen
                        }
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          class="hidden"
                          (change)="onImageSelected($event)"
                          [disabled]="imageUploading"
                        />
                      </label>

                      @if (imagePreviewUrl) {
                        <button
                          type="button"
                          (click)="removeImage()"
                          class="px-3 py-1.5 text-sm rounded-lg font-medium transition-colors text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20"
                        >
                          Quitar
                        </button>
                      }
                    </div>

                    <!-- imageUrl URL (manual fallback / external URL) -->
                    <input
                      type="text"
                      (input)="loadImageUrl($event)"
                      formControlName="imageUrl"
                      placeholder="o pega una URL: https://..."
                      class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      [class.border-error-500]="hasError('imageUrl')"
                    />
                    @if (hasError('imageUrl')) {
                      <p class="mt-1 text-xs text-error-600">
                        {{ getError('imageUrl') }}
                      </p>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Botones de accion -->
        <div
          class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 mt-6"
        >
          <!-- Hint del motivo del disabled (solo si hay errores y se tocó el form) -->
          @if (form.invalid && form.touched && !saving) {
            <p class="text-xs text-error-500 mr-auto flex items-center gap-1.5">
              <svg
                class="w-4 h-4 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              {{ disabledReason }}
            </p>
          }

          <app-button
            variant="outline"
            type="button"
            (btnClick)="cancelar()"
          >
            Cancelar
          </app-button>
          <app-button
            variant="primary"
            type="button"
            (btnClick)="guardar()"
            [disabled]="saving || form.invalid"
          >
            {{
              saving
                ? 'Guardando...'
                : isEditing
                  ? 'Actualizar'
                  : 'Crear Producto'
            }}
          </app-button>
        </div>
      </form>

      <!-- Sub-entidades (solo edit + page mode) -->
      @if (isEditing && !drawerMode && productId) {
        <div
          class="mt-8 space-y-3 border-t border-gray-200 dark:border-gray-700 pt-6"
        >
          <h3
            class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
          >
            Configuración avanzada
          </h3>

          <!-- Attachments -->
          <div
            class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <button
              type="button"
              (click)="showAttachments = !showAttachments"
              class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <span class="text-sm font-medium text-gray-800 dark:text-gray-200"
                >Adjuntos</span
              >
              <svg
                class="w-4 h-4 text-gray-500 transition-transform"
                [class.rotate-180]="showAttachments"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            @if (showAttachments) {
              <div class="p-4">
                <app-producto-attachments [productId]="productId" />
              </div>
            }
          </div>

          <!-- Tier prices: solo si el producto usa precios escalonados -->
          @if (form.get('usesTierPricing')?.value) {
          <div
            class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <button
              type="button"
              (click)="showTierPrices = !showTierPrices"
              class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <span class="text-sm font-medium text-gray-800 dark:text-gray-200"
                >Precios escalonados</span
              >
              <svg
                class="w-4 h-4 text-gray-500 transition-transform"
                [class.rotate-180]="showTierPrices"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            @if (showTierPrices) {
              <div class="p-4">
                <app-producto-tier-prices
                  [productId]="productId"
                  [businessId]="businessId"
                  [currency]="currentProduct?.currencyCode"
                />
              </div>
            }
          </div>
          }

          <!-- Variant attributes -->
          <div
            class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <button
              type="button"
              (click)="showVariantAttributes = !showVariantAttributes"
              class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <span class="text-sm font-medium text-gray-800 dark:text-gray-200"
                >Atributos de variantes</span
              >
              <svg
                class="w-4 h-4 text-gray-500 transition-transform"
                [class.rotate-180]="showVariantAttributes"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            @if (showVariantAttributes) {
              <div class="p-4">
                <app-producto-variant-attributes
                  [productId]="productId"
                  [businessId]="businessId"
                />
              </div>
            }
          </div>

          <!-- Variants -->
          <div
            class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <button
              type="button"
              (click)="showVariants = !showVariants"
              class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <span class="text-sm font-medium text-gray-800 dark:text-gray-200"
                >Variantes</span
              >
              <svg
                class="w-4 h-4 text-gray-500 transition-transform"
                [class.rotate-180]="showVariants"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            @if (showVariants) {
              <div class="p-4">
                <app-producto-variants
                  [productId]="productId"
                  [businessId]="businessId"
                  [currency]="currentProduct?.currencyCode"
                />
              </div>
            }
          </div>

          <!-- Price currencies -->
          <div
            class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <button
              type="button"
              (click)="showPriceCurrencies = !showPriceCurrencies"
              class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <span class="text-sm font-medium text-gray-800 dark:text-gray-200"
                >Precios por moneda</span
              >
              <svg
                class="w-4 h-4 text-gray-500 transition-transform"
                [class.rotate-180]="showPriceCurrencies"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            @if (showPriceCurrencies) {
              <div class="p-4">
                <app-producto-price-currencies
                  [productId]="productId"
                  [businessId]="businessId"
                />
              </div>
            }
          </div>
        </div>
      }
    }
  </div>
</ng-template>
